import { useCallback, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../shared/PrivateRoutes";
import { useMasqueradeMutation } from "../graphql/graphql";
import { Auth } from "../utils/Auth";
/**
 * Sets up global listeners for user activity (mouse/keyboard/touch/scroll)
 * and triggers a timeout after a period of inactivity.
 *
 * How it works (high-level):
 * - We start a timeout when the hook mounts.
 * - We listen to common user activity events on window (mousemove, keydown, etc.)
 *   and to 'visibilitychange' on document (because it's a document-level event).
 * - Any activity resets the timeout, so the user stays logged in while active.
 * - When the timeout fires, we run a TODO spot for you to plug in logout.
 *
 * Usage:
 * useInactivityLogout();
 */
const auth = new Auth();
export const useInactivityLogout = ({ timeoutMs = 5 * 60 * 1000, enabled = true, onSessionChange } = {}) => {
    const navigate = useNavigate();
    const { isMasquerading } = useContext(AppContext);
    const [, masquerade] = useMasqueradeMutation();
    const timerRef = useRef(null);
    const enabledRef = useRef(enabled);
    const isMasqueradingRef = useRef(isMasquerading);
    useEffect(() => {
        enabledRef.current = enabled;
    }, [enabled]);
    useEffect(() => {
        isMasqueradingRef.current = isMasquerading;
    }, [isMasquerading]);
    const clearExistingTimer = useCallback(() => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);
    const startTimer = useCallback(() => {
        if (!enabledRef.current)
            return;
        clearExistingTimer();
        timerRef.current = window.setTimeout(async () => {
            if (isMasqueradingRef.current) {
                await masquerade({ organizationId: null });
            }
            onSessionChange?.();
            auth.logout();
            navigate("/login");
        }, timeoutMs);
    }, [clearExistingTimer, timeoutMs, masquerade, navigate, onSessionChange]);
    // Public-friendly alias for restarting the timer on demand.
    const resetTimer = useCallback(() => {
        if (!enabledRef.current)
            return;
        startTimer();
    }, [startTimer]);
    // Cancels the timer (used during cleanup or when disabling the hook).
    const stopTimer = useCallback(() => {
        clearExistingTimer();
    }, [clearExistingTimer]);
    useEffect(() => {
        if (!enabled)
            return;
        // Global activity listeners on window cover most user inputs.
        const windowActivityEvents = [
            "mousemove",
            "mousedown",
            "keydown",
            "scroll",
            "touchstart",
            "wheel"
        ];
        // Any activity resets the timer. If the tab is hidden, ignore visibility changes.
        const handleActivity = () => {
            // Avoid immediate resets while the page is hidden to prevent churn on tab switches.
            if (document.visibilityState === "hidden")
                return;
            resetTimer();
        };
        windowActivityEvents.forEach((event) => window.addEventListener(event, handleActivity, { passive: true }));
        // 'visibilitychange' is dispatched on document, not window.
        document.addEventListener("visibilitychange", handleActivity);
        // Start on mount
        startTimer();
        // Remove all listeners and stop the timer on unmount.
        return () => {
            windowActivityEvents.forEach((event) => window.removeEventListener(event, handleActivity));
            document.removeEventListener("visibilitychange", handleActivity);
            stopTimer();
        };
    }, [enabled, resetTimer, startTimer, stopTimer]);
    return { startTimer, resetTimer, stopTimer };
};
